import React from "react";
import {
  ShowerHead,
  Dog,
  UtensilsCrossed,
  Trees,
  Waves,
  Beer,
  ShoppingBasket,
  Footprints,
} from "lucide-react";
import { LuxeOverline } from "./LuxeOverline";
import { useContent } from "@/lib/content";

const items = [
  {
    icon: ShowerHead,
    title: "Modern shower blocks",
    desc: "Two unisex blocks kept spotless. Includes an accessible wet room.",
    span: "md:col-span-2 md:row-span-2",
    tone: "primary",
  },
  {
    icon: UtensilsCrossed,
    title: "Dishwashing",
    desc: "On-site washing-up area with hot water.",
    tone: "cream",
  },
  {
    icon: Dog,
    title: "Enclosed dog trail",
    desc: "Half-mile secure walking loop for touring guests.",
    span: "md:col-span-2",
    tone: "accent",
  },
  {
    icon: Trees,
    title: "Landscaped grounds",
    desc: "Nine acres of countryside, mature trees and open skies.",
    tone: "cream",
  },
  {
    icon: Waves,
    title: "Private lakes",
    desc: "Two guests-only fishing waters at the heart of the park.",
    tone: "cream",
  },
];

// Nearby amenities — a short drive from the park
const nearby = [
  {
    icon: UtensilsCrossed,
    title: "Restaurants nearby",
    desc: "Country kitchens, fish & chips and Sunday-roast pubs — all within a short drive.",
  },
  {
    icon: ShoppingBasket,
    title: "Groceries & shops",
    desc: "Local farm shop and full-size supermarkets just minutes away in Boston & Spalding.",
  },
  {
    icon: Beer,
    title: "Traditional pubs",
    desc: "Real-ale village inns with dog-friendly gardens close by.",
  },
  {
    icon: Footprints,
    title: "Walks into nature",
    desc: "Fenland footpaths, riverside trails and quiet country lanes straight from the gate.",
  },
];

const toneStyles = {
  primary: "bg-[#0F0E0E] text-[#F5EFE6] border-transparent",
  accent: "bg-[#B76E79] text-white border-transparent",
  cream: "bg-white border-[#DDD1BE] text-[#14100F]",
};

const Facilities = () => {
  const c = useContent();
  return (
    <section
      id="facilities"
      data-testid="facilities-section"
      className="relative py-24 md:py-36 bg-[#EAE0D0] overflow-hidden"
    >
      {/* Ambient rose-gold glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at 90% 30%, rgba(228,178,168,0.25) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <LuxeOverline>{c("facilities.overline", "Facilities")}</LuxeOverline>
            <h2 className="font-serif text-4xl md:text-5xl mt-8 text-[#14100F] leading-[1.05]">
              {c("facilities.title", "Everything you need. Nothing you don’t.")}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-6">
            <p className="text-[#3A322E] text-base md:text-lg leading-relaxed">
              {c(
                "facilities.body",
                "We’ve chosen quality over quantity. Clean, thoughtful facilities that make longer stays easy — without turning the park into a theme park."
              )}
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-4 auto-rows-[180px] gap-4 md:gap-5"
          data-testid="facilities-grid"
        >
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div
                key={it.title}
                data-testid={`facility-${it.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`rounded-2xl border p-6 md:p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-md flex flex-col justify-between ${
                  toneStyles[it.tone]
                } ${it.span || ""}`}
              >
                <Icon size={26} strokeWidth={1.3} />
                <div className="mt-6">
                  <h3
                    className={`font-serif text-2xl md:text-3xl leading-tight ${
                      it.tone === "cream" ? "text-[#14100F]" : ""
                    }`}
                  >
                    {it.title}
                  </h3>
                  <p
                    className={`mt-2 text-sm leading-relaxed ${
                      it.tone === "cream" ? "text-[#6A5F58]" : "opacity-85"
                    }`}
                  >
                    {it.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Nearby amenities strip */}
        <div className="mt-24">
          <div className="mb-12 flex flex-col items-center text-center">
            <LuxeOverline align="center">
              {c("nearby.overline", "Just outside the gate")}
            </LuxeOverline>
            <h3 className="font-serif text-3xl md:text-5xl mt-8 text-[#14100F] leading-[1.05] max-w-2xl">
              {c("nearby.title_pre", "Close by —")}{" "}
              <em className="italic rose-gold-text">
                {c("nearby.title_em", "eat, shop, wander.")}
              </em>
            </h3>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
            data-testid="nearby-grid"
          >
            {nearby.map((n) => {
              const Icon = n.icon;
              return (
                <div
                  key={n.title}
                  data-testid={`nearby-${n.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="relative bg-white border border-[#DDD1BE] rounded-2xl p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:border-[#B76E79]/40 overflow-hidden group"
                >
                  <span className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#B76E79] via-[#E4B2A8] to-[#C9A47E] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#F5EFE6] to-[#E4B2A8] border border-[#B76E79]/30">
                    <Icon size={22} strokeWidth={1.4} className="text-[#0F0E0E]" />
                  </span>
                  <h4 className="mt-5 font-serif text-xl md:text-2xl text-[#14100F] leading-tight">
                    {n.title}
                  </h4>
                  <p className="mt-2 text-sm text-[#6A5F58] leading-relaxed">
                    {n.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Facilities;
